import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getStoredUser } from '../../hooks/useAuth';

const TIME_SLOTS = [
  { id: 'morning', label: '09:00 – 13:00', sub: 'Утро', icon: 'ri-sun-line' },
  { id: 'day', label: '13:00 – 18:00', sub: 'День', icon: 'ri-sun-foggy-line' },
  { id: 'evening', label: '18:00 – 22:00', sub: 'Вечер', icon: 'ri-moon-line' },
] as const;

type SlotId = (typeof TIME_SLOTS)[number]['id'];

const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
const MONTHS = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
];

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function isWeekend(d: Date): boolean {
  const wd = d.getDay(); // 0 = Вс, 6 = Сб
  return wd === 0 || wd === 6;
}

function sameDay(a: Date | null, b: Date | null): boolean {
  if (!a || !b) return false;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function formatHuman(d: Date): string {
  return d.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    weekday: 'long',
  });
}

function toISODate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Inline month calendar — past dates and today are disabled (earliest = tomorrow). */
function Calendar({
  value,
  onSelect,
}: {
  value: Date | null;
  onSelect: (d: Date) => void;
}) {
  const today = useMemo(() => startOfDay(new Date()), []);
  const minDate = useMemo(() => {
    const m = new Date(today);
    m.setDate(m.getDate() + 1);
    return m;
  }, [today]);

  const [viewMonth, setViewMonth] = useState(() => {
    const base = value ?? minDate;
    return new Date(base.getFullYear(), base.getMonth(), 1);
  });

  const days = useMemo(() => {
    const firstDay = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 1);
    const startWeekday = (firstDay.getDay() + 6) % 7; // make Monday = 0
    const daysInMonth = new Date(
      viewMonth.getFullYear(),
      viewMonth.getMonth() + 1,
      0,
    ).getDate();

    const cells: (Date | null)[] = [];
    for (let i = 0; i < startWeekday; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push(new Date(viewMonth.getFullYear(), viewMonth.getMonth(), d));
    }
    return cells;
  }, [viewMonth]);

  // Can't navigate before the current month
  const canGoPrev =
    viewMonth.getFullYear() > today.getFullYear() ||
    (viewMonth.getFullYear() === today.getFullYear() &&
      viewMonth.getMonth() > today.getMonth());

  return (
    <div className="bg-[#1a1d28] border border-[#2a2d3e] rounded-2xl p-4 w-full">
      {/* Month nav */}
      <div className="flex items-center justify-between mb-4">
        <button
          type="button"
          disabled={!canGoPrev}
          onClick={() =>
            setViewMonth(
              new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1),
            )
          }
          className="w-8 h-8 flex items-center justify-center rounded-lg border border-[#2a2d3e] text-slate-400 hover:text-white hover:border-[#3a3d4e] disabled:opacity-25 disabled:cursor-not-allowed transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-s-line" />
        </button>
        <span className="text-sm font-semibold text-white">
          {MONTHS[viewMonth.getMonth()]} {viewMonth.getFullYear()}
        </span>
        <button
          type="button"
          onClick={() =>
            setViewMonth(
              new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1),
            )
          }
          className="w-8 h-8 flex items-center justify-center rounded-lg border border-[#2a2d3e] text-slate-400 hover:text-white hover:border-[#3a3d4e] transition-colors cursor-pointer"
        >
          <i className="ri-arrow-right-s-line" />
        </button>
      </div>

      {/* Weekday header */}
      <div className="grid grid-cols-7 gap-1 mb-1">
        {WEEKDAYS.map((w) => (
          <div
            key={w}
            className="h-8 flex items-center justify-center text-[11px] font-medium text-slate-600"
          >
            {w}
          </div>
        ))}
      </div>

      {/* Day cells */}
      <div className="grid grid-cols-7 gap-1">
        {days.map((d, i) => {
          if (!d) return <div key={`e${i}`} />;
          const disabled = d < minDate || isWeekend(d);
          const selected = sameDay(d, value);
          const isToday = sameDay(d, today);
          return (
            <button
              key={toISODate(d)}
              type="button"
              disabled={disabled}
              onClick={() => onSelect(d)}
              className={[
                'h-9 rounded-lg text-sm font-medium transition-all cursor-pointer relative',
                disabled
                  ? 'text-slate-700 cursor-not-allowed'
                  : selected
                    ? 'bg-red-500 text-white'
                    : 'text-slate-300 hover:bg-[#252836]',
              ].join(' ')}
            >
              {d.getDate()}
              {isToday && !selected && (
                <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-red-400" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function MeetingPage() {
  const navigate = useNavigate();
  const user = getStoredUser();

  const [location, setLocation] = useState('');
  const [date, setDate] = useState<Date | null>(null);
  const [slot, setSlot] = useState<SlotId | null>(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user) navigate('/', { replace: true });
  }, [user, navigate]);

  const canSubmit = location.trim().length > 3 && !!date && !!slot;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || !date || !slot || !user || submitting) return;
    setError('');
    setSubmitting(true);

    try {
      const res = await fetch('/api/portal/meeting', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: user.username,
          location: location.trim(),
          date: toISODate(date),
          time_slot: slot, // 'morning' | 'day' | 'evening'
        }),
      });

      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.ok) {
        setError(data?.error ?? 'Не удалось отправить заявку. Попробуйте ещё раз.');
        return;
      }

      setSubmitted(true);
    } catch {
      setError('Нет связи с сервером. Проверьте интернет и попробуйте снова.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <Shell onBack={() => navigate('/chat-operator')}>
        <div className="max-w-lg mx-auto text-center pt-10">
          <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-emerald-500/10 border border-emerald-500/20 mx-auto mb-6">
            <i className="ri-check-line text-3xl text-emerald-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-3">Заявка отправлена</h2>
          <p className="text-slate-500 text-sm leading-relaxed mb-8">
            Ваша заявка на встречу с представителем Альфа-Банка зарегистрирована.
            Менеджер свяжется с вами для подтверждения деталей.
          </p>

          <div className="bg-[#13161f] border border-[#1e2130] rounded-2xl p-6 text-left space-y-4 mb-8">
            <SummaryRow icon="ri-map-pin-line" label="Место встречи" value={location.trim()} />
            <SummaryRow
              icon="ri-calendar-event-line"
              label="Дата"
              value={date ? formatHuman(date) : ''}
            />
            <SummaryRow
              icon="ri-time-line"
              label="Время"
              value={TIME_SLOTS.find((s) => s.id === slot)?.label ?? ''}
            />
          </div>

          <button
            onClick={() => navigate('/chat-operator')}
            className="w-full bg-emerald-500 hover:bg-emerald-400 text-white font-semibold rounded-xl py-3.5 text-sm transition-all cursor-pointer"
          >
            Вернуться в кабинет
          </button>
        </div>
      </Shell>
    );
  }

  return (
    <Shell onBack={() => navigate('/chat-operator')}>
      <div className="max-w-xl mx-auto">
        {/* Bank header */}
        <div className="flex items-start gap-4 bg-gradient-to-br from-red-500/10 to-transparent border border-red-500/20 rounded-2xl px-5 py-5 mb-8">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-red-500/15 border border-red-500/25 flex-shrink-0">
            <i className="ri-bank-line text-2xl text-red-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white mb-1">
              Встреча с представителем Альфа-Банка
            </h2>
            <p className="text-slate-500 text-sm leading-relaxed">
              Заполните детали встречи для оформления банковских реквизитов.
              Представитель приедет в удобное вам место и время.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-7">
          {/* Место встречи */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              <span className="text-red-400 mr-1">*</span>Место встречи
            </label>
            <p className="text-xs text-slate-600 mb-3">
              Полный адрес: город, улица, дом, квартира/офис
            </p>
            <textarea
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              rows={3}
              placeholder="г. Москва, ул. Тверская, д. 10, кв. 25"
              className="w-full bg-[#1a1d28] border border-[#2a2d3e] rounded-xl px-4 py-3.5 text-sm text-slate-200 placeholder-slate-700 focus:outline-none focus:border-red-500 transition-colors resize-none"
            />
          </div>

          {/* Дата встречи */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              <span className="text-red-400 mr-1">*</span>Дата встречи
            </label>
            <p className="text-xs text-slate-600 mb-3">
              Доступна запись начиная с завтрашнего дня
            </p>
            <button
              type="button"
              onClick={() => setCalendarOpen((o) => !o)}
              className={[
                'w-full flex items-center gap-3 bg-[#1a1d28] border rounded-xl px-4 py-3.5 text-sm transition-colors cursor-pointer',
                calendarOpen ? 'border-red-500' : 'border-[#2a2d3e] hover:border-[#3a3d4e]',
              ].join(' ')}
            >
              <span className="w-5 h-5 flex items-center justify-center text-red-400">
                <i className="ri-calendar-event-line text-base" />
              </span>
              <span className={date ? 'text-slate-200' : 'text-slate-700'}>
                {date ? formatHuman(date) : 'Выберите дату'}
              </span>
              <span className="ml-auto w-5 h-5 flex items-center justify-center text-slate-600">
                <i className={`ri-arrow-down-s-line transition-transform ${calendarOpen ? 'rotate-180' : ''}`} />
              </span>
            </button>

            {calendarOpen && (
              <div className="mt-3">
                <Calendar
                  value={date}
                  onSelect={(d) => {
                    setDate(d);
                    setCalendarOpen(false);
                  }}
                />
              </div>
            )}
          </div>

          {/* Время встречи */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              <span className="text-red-400 mr-1">*</span>Время встречи
            </label>
            <p className="text-xs text-slate-600 mb-3">
              Выберите удобный временной промежуток
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {TIME_SLOTS.map((s) => {
                const active = slot === s.id;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setSlot(s.id)}
                    className={[
                      'flex flex-col items-center gap-2 rounded-xl px-3 py-4 transition-all cursor-pointer border',
                      active
                        ? 'bg-red-500/10 border-red-500 text-white'
                        : 'bg-[#1a1d28] border-[#2a2d3e] text-slate-400 hover:border-[#3a3d4e] hover:text-slate-200',
                    ].join(' ')}
                  >
                    <i
                      className={`${s.icon} text-xl ${active ? 'text-red-400' : 'text-slate-600'}`}
                    />
                    <span className="text-sm font-semibold">{s.label}</span>
                    <span className="text-[11px] text-slate-600">{s.sub}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {error && (
            <p className="text-sm text-red-400 flex items-center gap-2">
              <i className="ri-error-warning-line" />
              {error}
            </p>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={!canSubmit || submitting}
            className={[
              'w-full flex items-center justify-center gap-2 rounded-xl py-4 text-sm font-semibold transition-all',
              canSubmit && !submitting
                ? 'bg-red-500 hover:bg-red-400 text-white cursor-pointer'
                : 'bg-[#1a1d28] border border-[#2a2d3e] text-slate-700 cursor-not-allowed',
            ].join(' ')}
          >
            {submitting ? (
              <>
                <i className="ri-loader-4-line animate-spin" />
                Отправляем…
              </>
            ) : (
              <>
                <i className="ri-send-plane-fill" />
                Отправить заявку на встречу
              </>
            )}
          </button>
        </form>
      </div>
    </Shell>
  );
}

function SummaryRow({
  icon,
  label,
  value,
}: {
  icon: string;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#1a1d28] text-red-400 flex-shrink-0">
        <i className={icon} />
      </span>
      <div className="min-w-0">
        <p className="text-xs text-slate-600 mb-0.5">{label}</p>
        <p className="text-sm text-slate-200 break-words">{value}</p>
      </div>
    </div>
  );
}

/** Page chrome matching the portal theme (neutral branding + back nav). */
function Shell({
  children,
  onBack,
}: {
  children: React.ReactNode;
  onBack: () => void;
}) {
  return (
    <div
      className="min-h-screen bg-[#0d0f18] flex flex-col"
    >
      {/* Top bar */}
      <header className="flex items-center gap-4 px-6 lg:px-8 py-5 border-b border-[#1e2130] sticky top-0 bg-[#0d0f18] z-20">
        <button
          onClick={onBack}
          className="w-9 h-9 flex items-center justify-center rounded-xl border border-[#2a2d3e] text-slate-400 hover:text-white hover:border-[#3a3d4e] transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-line" />
        </button>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-emerald-500/10 border border-emerald-500/20">
            <i className="ri-shield-user-line text-lg text-emerald-400" />
          </div>
          <div className="hidden sm:flex flex-col leading-tight">
            <span className="text-[11px] text-slate-500 tracking-widest uppercase">Корпоративный</span>
            <span className="text-sm font-semibold text-white tracking-wide">Портал сотрудников</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 px-6 lg:px-10 py-10 overflow-y-auto">{children}</main>
    </div>
  );
}
